import React, { ReactNode, useContext, useEffect, useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import type { NodeInstance, NodeInstanceDataPort, NodeInstanceState } from '@glueapp/graph-types';
import type { FormField, FormElement, FormFieldType } from '@glueapp/graph-types';
import { OutPortList } from '@/GraphEditor/drawingLayer/nodeComposition/OutPortList';
import { NodeEventInsulator } from '@/GraphEditor/drawingLayer/graphComponents/NodeInterfaceEventInsulator';
import { useTriggerRun } from '@/GraphEditor/drawingLayer/contexts/TriggerRunContext';
import { useNodeSelected } from '@/GraphEditor/drawingLayer/contexts/NodeSelectedContext';
import { RainbowBackgroundAnimation } from '@/base/GlueRainbowBackgroundAnimation';
import { ArrowIcon } from '@/base/ArrowIcon';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useFormApi } from '@/api/useFormApi';
import {
    Type,
    Hash,
    Mail,
    AlignLeft,
    List,
    CheckSquare,
    CircleDot,
    Calendar,
    Link,
    Phone,
    ToggleLeft,
    SlidersHorizontal,
    Star,
    Minus,
    Pencil,
    X,
    Copy,
    Check,
    Plus,
    ExternalLink,
    ChevronUp,
    ChevronDown,
} from 'lucide-react';
import { ThemeContext } from 'styled-components';
import { ThemeType } from '@/theme/glueTheme';
import { getEnvironmentConfig } from '@/environmentConfig';

const RunNodeButton = styled.button`
    position: absolute;
    top: -40px;
    left: 4px;

    &:hover {
        cursor: pointer;
        opacity: 1;
        ${RainbowBackgroundAnimation}
    }

    opacity: 0.5;
    transition: opacity 0.3s ease;

    box-sizing: border-box;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    outline: none;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${props => props.theme.backgroundColor};
    border: 2px solid ${props => props.theme.separatorColor};
`;

const DropdownMenuItem = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 4px 8px;
    font-size: 12px;
    text-align: left;
    cursor: pointer;
    border-radius: 4px;
    color: ${props => props.theme.textColor};
    transition: background-color 150ms;

    &:hover {
        background-color: ${props => props.theme.elevatedBackgroundColor};
    }
`;

// --- Types ---

interface Props {
    name: string;
    nodeInstance: NodeInstance;
    nodeState: Record<string, unknown>;
    setNodeState: (state: NodeInstanceState) => void;
    renderInherentInPort: () => ReactNode;
    renderInherentOutPort: () => ReactNode;
}

interface FieldEditState {
    mode: 'edit' | 'create';
    fieldId?: string;
    type: FormFieldType;
    label: string;
    options: string[];
    optional: boolean;
}

const FIELD_TYPE_OPTIONS: { value: FormFieldType; label: string }[] = [
    { value: 'text', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'email', label: 'Email' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'select', label: 'Select' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'radio', label: 'Radio' },
    { value: 'date', label: 'Date' },
    { value: 'url', label: 'URL' },
    { value: 'phone', label: 'Phone' },
    { value: 'toggle', label: 'Toggle' },
    { value: 'slider', label: 'Slider' },
    { value: 'rating', label: 'Rating' },
];

function getFieldIcon(type: FormFieldType | 'separator') {
    const iconClass = 'h-3.5 w-3.5';
    switch (type) {
        case 'text':
            return <Type className={iconClass} />;
        case 'number':
            return <Hash className={iconClass} />;
        case 'email':
            return <Mail className={iconClass} />;
        case 'textarea':
            return <AlignLeft className={iconClass} />;
        case 'select':
            return <List className={iconClass} />;
        case 'checkbox':
            return <CheckSquare className={iconClass} />;
        case 'radio':
            return <CircleDot className={iconClass} />;
        case 'date':
            return <Calendar className={iconClass} />;
        case 'url':
            return <Link className={iconClass} />;
        case 'phone':
            return <Phone className={iconClass} />;
        case 'toggle':
            return <ToggleLeft className={iconClass} />;
        case 'slider':
            return <SlidersHorizontal className={iconClass} />;
        case 'rating':
            return <Star className={iconClass} />;
        case 'separator':
            return <Minus className={iconClass} />;
    }
}

function deriveNextFieldId(fields: FormElement[]): number {
    let max = 0;
    for (const field of fields) {
        const numId = parseInt(field.id, 10);
        if (!isNaN(numId) && numId > max) max = numId;
    }
    return max + 1;
}

function getFormUrl(formId: string | undefined): string {
    const baseUrl = getEnvironmentConfig().formsServiceUrl;
    if (!formId) return `${baseUrl}/...`;
    return `${baseUrl}/${formId}`;
}

// --- Section Label ---

const SectionLabel: React.FC<{ children: ReactNode; rightSlot?: ReactNode; labelColor: string }> = ({
    children,
    rightSlot,
    labelColor,
}) => (
    <div className="flex items-center justify-between px-3 py-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: labelColor }}>
            {children}
        </span>
        {rightSlot && <NodeEventInsulator>{rightSlot}</NodeEventInsulator>}
    </div>
);

// --- Select dismiss hook ---
// NodeEventInsulator stops pointerDown propagation, which prevents Radix's
// document-level dismiss listener from firing. This hook uses a capture-phase
// listener that fires before propagation is stopped.

function useSelectDismiss(open: boolean, setOpen: (open: boolean) => void) {
    useEffect(() => {
        if (!open) return;
        const handler = (event: PointerEvent) => {
            const target = event.target as HTMLElement;
            // Don't close if clicking inside the Select dropdown portal
            if (target.closest('[role="listbox"]')) return;
            setOpen(false);
        };
        document.addEventListener('pointerdown', handler, true);
        return () => document.removeEventListener('pointerdown', handler, true);
    }, [open, setOpen]);
}

// --- Field Edit Panel ---

const FieldEditPanel: React.FC<{
    state: FieldEditState;
    onChange: (state: FieldEditState) => void;
    onSave: () => void;
    onCancel: () => void;
}> = ({ state, onChange, onSave, onCancel }) => {
    const [typeSelectOpen, setTypeSelectOpen] = useState(false);
    const stableSetTypeSelectOpen = useCallback((open: boolean) => setTypeSelectOpen(open), []);
    useSelectDismiss(typeSelectOpen, stableSetTypeSelectOpen);

    const updateField = (partial: Partial<FieldEditState>) => {
        onChange({ ...state, ...partial });
    };

    const handleTypeChange = (newType: FormFieldType) => {
        const updates: Partial<FieldEditState> = { type: newType };
        if ((newType === 'select' || newType === 'radio') && state.options.length === 0) {
            updates.options = ['Option 1', 'Option 2'];
        }
        if (newType !== 'select' && newType !== 'radio') {
            updates.options = [];
        }
        updateField(updates);
    };

    return (
        <div className="mx-2 my-1 p-3 rounded-md border border-border bg-muted/20">
            <NodeEventInsulator>
                {/* Type + Label */}
                <div className="flex items-start gap-2 mb-2">
                    <div className="w-[110px] shrink-0">
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Type</label>
                        <Select
                            open={typeSelectOpen}
                            onOpenChange={setTypeSelectOpen}
                            value={state.type}
                            onValueChange={value => handleTypeChange(value as FormFieldType)}
                        >
                            <SelectTrigger className="h-7 text-[11px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {FIELD_TYPE_OPTIONS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                                        {opt.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="flex-1 min-w-0">
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Name</label>
                        <Input
                            className="h-7 text-sm font-medium"
                            value={state.label}
                            placeholder="Field name"
                            onChange={event => updateField({ label: event.target.value })}
                            onKeyDown={event => {
                                if (event.key === 'Enter' && state.label.trim()) onSave();
                            }}
                            autoFocus
                        />
                    </div>
                </div>

                {/* Options editor for select/radio */}
                {(state.type === 'select' || state.type === 'radio') && (
                    <div className="mb-2 space-y-1.5">
                        <span className="text-[10px] text-muted-foreground font-medium">Options</span>
                        {state.options.map((option: string, index: number) => (
                            <div key={index} className="flex items-center gap-1">
                                <Input
                                    className="h-6 flex-1 min-w-0 text-[11px]"
                                    value={option}
                                    onChange={event => {
                                        const newOptions = [...state.options];
                                        newOptions[index] = event.target.value;
                                        updateField({ options: newOptions });
                                    }}
                                    placeholder={`Option ${index + 1}`}
                                />
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 shrink-0"
                                    onClick={() => {
                                        const newOptions = [...state.options];
                                        newOptions.splice(index, 1);
                                        if (newOptions.length === 0) newOptions.push('Option 1');
                                        updateField({ options: newOptions });
                                    }}
                                >
                                    <X className="h-3 w-3" />
                                </Button>
                            </div>
                        ))}
                        <button
                            className="text-[10px] text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                            onClick={() => {
                                updateField({ options: [...state.options, `Option ${state.options.length + 1}`] });
                            }}
                        >
                            + Add option
                        </button>
                    </div>
                )}

                {/* Optional toggle + Save/Cancel */}
                <div className="flex items-center justify-between pt-1">
                    <label className="flex items-center gap-1.5 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={state.optional}
                            onChange={event => updateField({ optional: event.target.checked })}
                        />
                        <span className="text-[11px]">Optional</span>
                    </label>
                    <div className="flex items-center gap-1.5">
                        <Button variant="ghost" size="sm" className="h-7 text-[11px]" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button size="sm" className="h-7 text-[11px]" onClick={onSave} disabled={!state.label.trim()}>
                            {state.mode === 'create' ? 'Add' : 'Save'}
                        </Button>
                    </div>
                </div>
            </NodeEventInsulator>
        </div>
    );
};

// --- WYSIWYG Field Preview (view-only) ---

const FieldPreview: React.FC<{
    field: FormField;
    isFirst: boolean;
    isLast: boolean;
    onEdit: () => void;
    onRemove: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
}> = ({ field, isFirst, isLast, onEdit, onRemove, onMoveUp, onMoveDown }) => {
    const renderFieldInput = () => {
        switch (field.type) {
            case 'textarea':
                return (
                    <div className="w-full h-16 rounded border border-border bg-muted/50 px-2 py-1.5 text-[11px] text-muted-foreground">
                        {field.placeholder || 'Type here...'}
                    </div>
                );
            case 'checkbox':
                return (
                    <div className="flex items-center gap-2">
                        <div className="h-4 w-4 rounded border border-border" />
                        <span className="text-[11px] text-muted-foreground">{field.placeholder || 'Check this'}</span>
                    </div>
                );
            case 'toggle':
                return (
                    <div className="flex items-center gap-2">
                        <div className="h-5 w-9 rounded-full bg-border relative">
                            <div className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-foreground/40" />
                        </div>
                        {field.placeholder && <span className="text-[11px] text-muted-foreground">{field.placeholder}</span>}
                    </div>
                );
            case 'select':
                return (
                    <div className="w-full h-8 rounded border border-border bg-muted/50 px-2 flex items-center justify-between">
                        <span className="text-[11px] text-muted-foreground">{field.placeholder || 'Select...'}</span>
                        <List className="h-3 w-3 text-muted-foreground" />
                    </div>
                );
            case 'radio':
                return (
                    <div className="space-y-1">
                        {(field.options || ['Option 1', 'Option 2']).slice(0, 3).map((option: string, index: number) => (
                            <div key={index} className="flex items-center gap-2">
                                <div className="h-3.5 w-3.5 rounded-full border border-border" />
                                <span className="text-[11px] text-muted-foreground">{option}</span>
                            </div>
                        ))}
                    </div>
                );
            case 'slider':
                return (
                    <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 rounded-full bg-border relative">
                            <div className="absolute left-1/3 top-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-foreground/40" />
                        </div>
                        <span className="text-[11px] text-muted-foreground w-6 text-right">50</span>
                    </div>
                );
            case 'rating':
                return (
                    <div className="flex gap-0.5">
                        {Array.from({ length: 5 }, (_, index) => (
                            <Star
                                key={index}
                                className={`h-4 w-4 ${index < 3 ? 'fill-foreground/30 text-foreground/30' : 'text-muted-foreground/40'}`}
                            />
                        ))}
                    </div>
                );
            case 'date':
                return (
                    <div className="w-full h-8 rounded border border-border bg-muted/50 px-2 flex items-center justify-between">
                        <span className="text-[11px] text-muted-foreground">mm/dd/yyyy</span>
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                    </div>
                );
            default:
                return (
                    <div className="w-full h-8 rounded border border-border bg-muted/50 px-2 flex items-center">
                        <span className="text-[11px] text-muted-foreground">{field.placeholder || 'Type here...'}</span>
                    </div>
                );
        }
    };

    return (
        <div className="group/field relative px-3 py-2">
            {/* Hover overlay with controls */}
            <div className="absolute inset-0 opacity-0 group-hover/field:opacity-100 transition-opacity z-10 pointer-events-none">
                <div className="absolute top-1 right-1 flex items-center gap-0.5 pointer-events-auto">
                    <NodeEventInsulator>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 bg-background/80 backdrop-blur-sm"
                            disabled={isFirst}
                            onClick={onMoveUp}
                        >
                            <ChevronUp className="h-2.5 w-2.5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 bg-background/80 backdrop-blur-sm"
                            disabled={isLast}
                            onClick={onMoveDown}
                        >
                            <ChevronDown className="h-2.5 w-2.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0 bg-background/80 backdrop-blur-sm" onClick={onEdit}>
                            <Pencil className="h-2.5 w-2.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0 bg-background/80 backdrop-blur-sm" onClick={onRemove}>
                            <X className="h-2.5 w-2.5" />
                        </Button>
                    </NodeEventInsulator>
                </div>
            </div>

            {/* Label */}
            <div className="mb-1">
                <span className="text-[12px] font-medium leading-none">
                    {field.label}
                    {field.optional && <span className="text-muted-foreground ml-1 text-[10px] font-normal">(optional)</span>}
                </span>
            </div>

            {/* Field preview */}
            {renderFieldInput()}
        </div>
    );
};

// --- Separator Preview ---

const SeparatorPreview: React.FC<{
    label?: string;
    isFirst: boolean;
    isLast: boolean;
    onRemove: () => void;
    onMoveUp: () => void;
    onMoveDown: () => void;
}> = ({ label, isFirst, isLast, onRemove, onMoveUp, onMoveDown }) => (
    <div className="group/field relative px-3 py-2">
        <div className="absolute inset-0 opacity-0 group-hover/field:opacity-100 transition-opacity z-10 pointer-events-none">
            <div className="absolute top-1 right-1 flex items-center gap-0.5 pointer-events-auto">
                <NodeEventInsulator>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 bg-background/80 backdrop-blur-sm"
                        disabled={isFirst}
                        onClick={onMoveUp}
                    >
                        <ChevronUp className="h-2.5 w-2.5" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0 bg-background/80 backdrop-blur-sm"
                        disabled={isLast}
                        onClick={onMoveDown}
                    >
                        <ChevronDown className="h-2.5 w-2.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0 bg-background/80 backdrop-blur-sm" onClick={onRemove}>
                        <X className="h-2.5 w-2.5" />
                    </Button>
                </NodeEventInsulator>
            </div>
        </div>
        {label && <p className="text-[10px] font-semibold uppercase tracking-wider opacity-40 mb-1">{label}</p>}
        <div className="border-t border-border" />
    </div>
);

const EMPTY_FIELDS: FormElement[] = [];

// --- Main Content Layout ---

export const FormTriggerContentLayout: React.FC<Props> = ({
    name,
    nodeInstance,
    nodeState,
    setNodeState,
    renderInherentInPort,
    renderInherentOutPort,
}) => {
    const triggerRun = useTriggerRun();
    const selected = useNodeSelected();
    const theme = useContext<ThemeType>(ThemeContext);

    const separatorStyle = { borderColor: theme.separatorColor };
    const labelColor = theme.subtleContrastColor;

    // --- graphId + Form API ---
    const { graphId } = useParams<{ graphId: string }>();
    const { getFormsForGraph, createForm: createFormApi, updateForm: updateFormApi } = useFormApi();

    // --- State from nodeInstance.state ---
    const fields: FormElement[] = (nodeState.fields as FormElement[]) || EMPTY_FIELDS;
    const formTitle: string = (nodeState.formTitle as string) ?? '';
    const formId: string | undefined = nodeState.formId as string | undefined;
    const successMessage: string = (nodeState.successMessage as string) || 'Thank you for your submission.';
    const redirectUrl: string = (nodeState.redirectUrl as string) || '';
    const formPassword: string = (nodeState.formPassword as string) || '';
    const accessMode: 'public' | 'private' = (nodeState.accessMode as 'public' | 'private') || 'public';
    const submitLabel: string = (nodeState.submitLabel as string) || 'Submit';

    // --- Backend form creation + sync ---
    // Refs to avoid stale closures in async callbacks and unstable API references in deps
    const nodeStateRef = useRef(nodeState);
    nodeStateRef.current = nodeState;
    const apiRef = useRef({ getFormsForGraph, createFormApi, updateFormApi });
    apiRef.current = { getFormsForGraph, createFormApi, updateFormApi };

    // Auto-create backend form when formId is missing
    const formCreationInProgress = useRef(false);
    useEffect(() => {
        if (formId || !graphId || formCreationInProgress.current) return;
        formCreationInProgress.current = true;

        const ensureForm = async () => {
            try {
                // Check if a form already exists for this node (e.g., page reload before formId was saved)
                const existingForms = await apiRef.current.getFormsForGraph(graphId);
                const existingForm = existingForms.find(form => form.nodeInstanceId === nodeInstance.id);

                if (existingForm) {
                    setNodeState({ ...nodeStateRef.current, formId: existingForm.id });
                    return;
                }

                // Create a new form
                const title = (nodeStateRef.current.formTitle as string) || 'Untitled Form';
                const form = await apiRef.current.createFormApi(graphId, nodeInstance.id, title);
                setNodeState({ ...nodeStateRef.current, formId: form.id });
            } catch {
                formCreationInProgress.current = false;
            }
        };

        ensureForm();
    }, [formId, graphId, nodeInstance.id, setNodeState]);

    // Sync form data to backend (debounced 1s after last change)
    const syncTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    useEffect(() => {
        if (!formId) return;

        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = setTimeout(() => {
            apiRef.current
                .updateFormApi(formId, {
                    title: formTitle,
                    fields: fields,
                    successMessage: successMessage || null,
                    redirectUrl: redirectUrl || null,
                    password: formPassword || null,
                    submitLabel: submitLabel || null,
                })
                .catch(() => {});
        }, 1000);

        return () => clearTimeout(syncTimeoutRef.current);
    }, [formId, formTitle, fields, successMessage, redirectUrl, formPassword, submitLabel]);

    // Editing / creating state
    const [fieldEdit, setFieldEdit] = useState<FieldEditState | null>(null);
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [copiedUrl, setCopiedUrl] = useState(false);
    const [editingFormTitle, setEditingFormTitle] = useState(false);
    const [editingSubmitLabel, setEditingSubmitLabel] = useState(false);
    const [accessSelectOpen, setAccessSelectOpen] = useState(false);
    const stableSetAccessSelectOpen = useCallback((open: boolean) => setAccessSelectOpen(open), []);
    useSelectDismiss(accessSelectOpen, stableSetAccessSelectOpen);

    // Add-field dropdown: stop wheel events from reaching the graph zoom handler
    const addMenuRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const element = addMenuRef.current;
        if (!element) return;
        const stopWheel = (event: WheelEvent) => {
            event.stopPropagation();
        };
        element.addEventListener('wheel', stopWheel, { passive: false });
        return () => element.removeEventListener('wheel', stopWheel);
    }, [showAddMenu]);

    // Add-field dropdown: close on outside click (capture phase fires before NodeEventInsulator stops propagation)
    useEffect(() => {
        if (!showAddMenu) return;
        const handler = (event: PointerEvent) => {
            if (addMenuRef.current?.contains(event.target as HTMLElement)) return;
            setShowAddMenu(false);
        };
        document.addEventListener('pointerdown', handler, true);
        return () => document.removeEventListener('pointerdown', handler, true);
    }, [showAddMenu]);

    // Output ports from nodeInstance
    const outPorts = nodeInstance.outPorts;

    // Form URL from environment config
    const formUrl = getFormUrl(formId);

    // --- State Update (computeShape derives ports) ---
    const updateFieldsAndShape = (newFields: FormElement[], extraState?: Record<string, unknown>) => {
        setNodeState({
            ...nodeState,
            fields: newFields,
            ...extraState,
        } as NodeInstanceState);
    };

    // --- Field Edit / Create Handlers ---

    const startEditing = (field: FormField) => {
        setFieldEdit({
            mode: 'edit',
            fieldId: field.id,
            type: field.type,
            label: field.label,
            options: field.options ? [...field.options] : [],
            optional: field.optional,
        });
    };

    const startCreating = (type: FormFieldType) => {
        setFieldEdit({
            mode: 'create',
            type,
            label: '',
            options: type === 'select' || type === 'radio' ? ['Option 1', 'Option 2'] : [],
            optional: false,
        });
        setShowAddMenu(false);
    };

    const deduplicateLabel = (label: string, excludeFieldId?: string): string => {
        const existingLabels = new Set(
            fields.filter(field => field.type !== 'separator' && field.id !== excludeFieldId).map(field => (field as FormField).label),
        );
        let finalLabel = label;
        if (existingLabels.has(finalLabel)) {
            let counter = 2;
            while (existingLabels.has(`${finalLabel} ${counter}`)) counter++;
            finalLabel = `${finalLabel} ${counter}`;
        }
        return finalLabel;
    };

    const saveFieldEdit = () => {
        if (!fieldEdit) return;
        const trimmed = fieldEdit.label.trim();
        if (!trimmed) return;

        if (fieldEdit.mode === 'edit' && fieldEdit.fieldId) {
            const finalLabel = deduplicateLabel(trimmed, fieldEdit.fieldId);
            const newFields = fields.map(field => {
                if (field.id === fieldEdit.fieldId && field.type !== 'separator') {
                    const updated: FormField = {
                        ...(field as FormField),
                        type: fieldEdit.type,
                        label: finalLabel,
                        optional: fieldEdit.optional,
                    };
                    if (fieldEdit.type === 'select' || fieldEdit.type === 'radio') {
                        updated.options = fieldEdit.options;
                    } else {
                        delete updated.options;
                    }
                    return updated;
                }
                return field;
            });
            updateFieldsAndShape(newFields);
        } else {
            const finalLabel = deduplicateLabel(trimmed);
            const nextId = deriveNextFieldId(fields);
            const newField: FormField = {
                id: String(nextId),
                type: fieldEdit.type,
                label: finalLabel,
                optional: fieldEdit.optional,
            };
            if (fieldEdit.type === 'select' || fieldEdit.type === 'radio') {
                newField.options = fieldEdit.options;
            }
            const newFields = [...fields, newField];
            updateFieldsAndShape(newFields);
        }

        setFieldEdit(null);
    };

    const cancelFieldEdit = () => {
        setFieldEdit(null);
    };

    // --- Simple Field Actions (from hover controls) ---

    const addSeparator = () => {
        const nextId = deriveNextFieldId(fields);
        const newFields: FormElement[] = [...fields, { id: String(nextId), type: 'separator' }];
        updateFieldsAndShape(newFields);
        setShowAddMenu(false);
    };

    const removeField = (fieldId: string) => {
        if (fieldEdit?.fieldId === fieldId) setFieldEdit(null);
        const newFields = fields.filter(field => field.id !== fieldId);
        updateFieldsAndShape(newFields);
    };

    const moveFieldUp = (fieldId: string) => {
        const index = fields.findIndex(field => field.id === fieldId);
        if (index <= 0) return;
        const newFields = [...fields];
        [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
        updateFieldsAndShape(newFields);
    };

    const moveFieldDown = (fieldId: string) => {
        const index = fields.findIndex(field => field.id === fieldId);
        if (index < 0 || index >= fields.length - 1) return;
        const newFields = [...fields];
        [newFields[index + 1], newFields[index]] = [newFields[index], newFields[index + 1]];
        updateFieldsAndShape(newFields);
    };

    const copyUrl = () => {
        navigator.clipboard.writeText(formUrl);
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
    };

    const openFormUrl = () => {
        if (formId) {
            window.open(formUrl, '_blank', 'noopener,noreferrer');
        }
    };

    // --- Render ---

    const renderManualTrigger = () => {
        if (selected) {
            return (
                <RunNodeButton aria-label={`Run block ${name}`} onClick={triggerRun} onPointerDown={event => event.stopPropagation()}>
                    <ArrowIcon />
                </RunNodeButton>
            );
        }
        return null;
    };

    return (
        <div style={{ minWidth: 600 }}>
            {renderManualTrigger()}
            {/* Header */}
            <div className="flex justify-between pt-2.5 pb-1">
                {renderInherentInPort()}
                <h2 className="m-0 font-medium text-[1.2em] grow text-left px-2">{name}</h2>
                {renderInherentOutPort()}
            </div>

            {/* Form URL — always visible */}
            <div className="px-3 pb-2">
                <NodeEventInsulator>
                    <div className="flex items-center gap-1 px-2 py-1 rounded bg-current/5 border border-current/10">
                        <span className="text-[10px] truncate opacity-60 flex-1 font-mono">{formUrl}</span>
                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0 shrink-0" onClick={copyUrl}>
                            {copiedUrl ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                        </Button>
                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0 shrink-0" onClick={openFormUrl} disabled={!formId}>
                            <ExternalLink className="h-3 w-3" />
                        </Button>
                    </div>
                </NodeEventInsulator>
            </div>

            {/* Two-column body: Form (left) | Outputs (right) */}
            <div className="flex border-t" style={separatorStyle}>
                {/* Left column — form builder */}
                <div className="flex-1 min-w-0">
                    {/* WYSIWYG Form Card */}
                    <div className="m-5 rounded-lg border bg-background shadow-sm" style={separatorStyle}>
                        {/* Form title inside preview */}
                        <div className="px-6 pt-6 pb-2">
                            {editingFormTitle ? (
                                <div className="group/title relative">
                                    <NodeEventInsulator>
                                        <Input
                                            className="h-8 text-sm font-semibold"
                                            value={formTitle}
                                            placeholder="Form title"
                                            autoFocus
                                            onChange={event => {
                                                setNodeState({ ...nodeState, formTitle: event.target.value });
                                            }}
                                            onKeyDown={event => {
                                                if (event.key === 'Enter' || event.key === 'Escape') {
                                                    setEditingFormTitle(false);
                                                }
                                            }}
                                            onBlur={() => setEditingFormTitle(false)}
                                        />
                                    </NodeEventInsulator>
                                    <div className="absolute top-1 right-1">
                                        <NodeEventInsulator>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-5 w-5 p-0 bg-background/80 backdrop-blur-sm"
                                                onMouseDown={event => {
                                                    event.preventDefault();
                                                    setEditingFormTitle(false);
                                                }}
                                            >
                                                <Check className="h-2.5 w-2.5" />
                                            </Button>
                                        </NodeEventInsulator>
                                    </div>
                                </div>
                            ) : (
                                <div className="group/title relative">
                                    <span className="text-sm font-semibold leading-8">
                                        {formTitle || <span className="opacity-40">Form title</span>}
                                    </span>
                                    <div className="absolute top-1 right-0 opacity-0 group-hover/title:opacity-100 transition-opacity">
                                        <NodeEventInsulator>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-5 w-5 p-0 bg-background/80 backdrop-blur-sm"
                                                onClick={() => setEditingFormTitle(true)}
                                            >
                                                <Pencil className="h-2.5 w-2.5" />
                                            </Button>
                                        </NodeEventInsulator>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Field previews */}
                        <div className="pb-2 px-3">
                            {fields.length === 0 && !fieldEdit && (
                                <div className="px-3 py-6 text-center text-[11px] opacity-40">Add fields to build your form</div>
                            )}

                            {fields.map((element, index) => {
                                if (element.type === 'separator') {
                                    return (
                                        <SeparatorPreview
                                            key={element.id}
                                            label={element.label}
                                            isFirst={index === 0}
                                            isLast={index === fields.length - 1}
                                            onRemove={() => removeField(element.id)}
                                            onMoveUp={() => moveFieldUp(element.id)}
                                            onMoveDown={() => moveFieldDown(element.id)}
                                        />
                                    );
                                }

                                const field = element as FormField;

                                // Replace with edit panel when this field is being edited
                                if (fieldEdit?.mode === 'edit' && fieldEdit.fieldId === field.id) {
                                    return (
                                        <FieldEditPanel
                                            key={field.id}
                                            state={fieldEdit}
                                            onChange={setFieldEdit}
                                            onSave={saveFieldEdit}
                                            onCancel={cancelFieldEdit}
                                        />
                                    );
                                }

                                return (
                                    <FieldPreview
                                        key={field.id}
                                        field={field}
                                        isFirst={index === 0}
                                        isLast={index === fields.length - 1}
                                        onEdit={() => startEditing(field)}
                                        onRemove={() => removeField(field.id)}
                                        onMoveUp={() => moveFieldUp(field.id)}
                                        onMoveDown={() => moveFieldDown(field.id)}
                                    />
                                );
                            })}

                            {/* Create-mode panel at bottom of field list */}
                            {fieldEdit?.mode === 'create' && (
                                <FieldEditPanel
                                    state={fieldEdit}
                                    onChange={setFieldEdit}
                                    onSave={saveFieldEdit}
                                    onCancel={cancelFieldEdit}
                                />
                            )}
                        </div>

                        {/* Submit button — editable label */}
                        <div className="px-6 pb-6 pt-3">
                            {editingSubmitLabel ? (
                                <div className="group/submit relative">
                                    <NodeEventInsulator>
                                        <Input
                                            className="h-9 text-sm font-medium text-center"
                                            value={submitLabel}
                                            placeholder="Submit"
                                            autoFocus
                                            onChange={event => {
                                                setNodeState({ ...nodeState, submitLabel: event.target.value });
                                            }}
                                            onKeyDown={event => {
                                                if (event.key === 'Enter' || event.key === 'Escape') {
                                                    setEditingSubmitLabel(false);
                                                }
                                            }}
                                            onBlur={() => setEditingSubmitLabel(false)}
                                        />
                                    </NodeEventInsulator>
                                    <div className="absolute top-1 right-1">
                                        <NodeEventInsulator>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-5 w-5 p-0 bg-background/80 backdrop-blur-sm"
                                                onMouseDown={event => {
                                                    event.preventDefault();
                                                    setEditingSubmitLabel(false);
                                                }}
                                            >
                                                <Check className="h-2.5 w-2.5" />
                                            </Button>
                                        </NodeEventInsulator>
                                    </div>
                                </div>
                            ) : (
                                <div className="group/submit relative">
                                    <div className="w-full h-9 rounded-md bg-primary flex items-center justify-center">
                                        <span className="text-primary-foreground text-sm font-medium">{submitLabel}</span>
                                    </div>
                                    <div className="absolute top-1 right-1 opacity-0 group-hover/submit:opacity-100 transition-opacity">
                                        <NodeEventInsulator>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-5 w-5 p-0 bg-background/80 backdrop-blur-sm"
                                                onClick={() => setEditingSubmitLabel(true)}
                                            >
                                                <Pencil className="h-2.5 w-2.5" />
                                            </Button>
                                        </NodeEventInsulator>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Add field button — outside card, hidden during edit/create */}
                    {!fieldEdit && (
                        <div className="px-5 pb-4 relative">
                            <NodeEventInsulator>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full h-7 text-[11px] gap-1"
                                    onClick={() => setShowAddMenu(!showAddMenu)}
                                >
                                    <Plus className="h-3 w-3" />
                                    Add Field
                                </Button>
                                {showAddMenu && (
                                    <div
                                        ref={addMenuRef}
                                        className="absolute left-3 right-3 top-full mt-1 z-50 rounded-md shadow-lg p-1 max-h-[200px] overflow-y-auto"
                                        style={{
                                            backgroundColor: theme.backgroundColor,
                                            border: `1px solid ${theme.separatorColor}`,
                                        }}
                                    >
                                        {FIELD_TYPE_OPTIONS.map(option => (
                                            <DropdownMenuItem key={option.value} onClick={() => startCreating(option.value)}>
                                                {getFieldIcon(option.value)}
                                                {option.label}
                                            </DropdownMenuItem>
                                        ))}
                                        <div className="my-1" style={{ borderTop: `1px solid ${theme.separatorColor}` }} />
                                        <DropdownMenuItem onClick={addSeparator}>
                                            {getFieldIcon('separator')}
                                            Separator
                                        </DropdownMenuItem>
                                    </div>
                                )}
                            </NodeEventInsulator>
                        </div>
                    )}
                </div>

                {/* Right column — outputs */}
                <div className="min-w-[140px] border-l pb-1" style={separatorStyle}>
                    <SectionLabel labelColor={labelColor}>Output</SectionLabel>
                    <OutPortList ports={outPorts} />
                </div>
            </div>

            {/* Settings — full width */}
            <div className="border-t" style={separatorStyle}>
                <SectionLabel labelColor={labelColor}>Settings</SectionLabel>
                <div className="px-3 pb-2 space-y-2">
                    <NodeEventInsulator>
                        {/* Access mode toggle */}
                        <div>
                            <label className="text-[10px] opacity-60 block mb-0.5">Access</label>
                            <Select
                                open={accessSelectOpen}
                                onOpenChange={setAccessSelectOpen}
                                value={accessMode}
                                onValueChange={(value: string) => {
                                    const newMode = value as 'public' | 'private';
                                    const newState = { ...nodeState, accessMode: newMode } as NodeInstanceState;
                                    if (newMode === 'public') {
                                        newState.formPassword = '';
                                    }
                                    setNodeState(newState);
                                }}
                            >
                                <SelectTrigger className="h-7 text-[11px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="public">Public</SelectItem>
                                    <SelectItem value="private">Private</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Password — only visible when private */}
                        {accessMode === 'private' && (
                            <div>
                                <label className="text-[10px] opacity-60 block mb-0.5">Password</label>
                                <Input
                                    className="h-7 text-xs"
                                    type="password"
                                    value={formPassword}
                                    placeholder="Enter password"
                                    onChange={event => {
                                        setNodeState({ ...nodeState, formPassword: event.target.value });
                                    }}
                                />
                            </div>
                        )}

                        {/* Success message */}
                        <div>
                            <label className="text-[10px] opacity-60 block mb-0.5">Success Message</label>
                            <Input
                                className="h-7 text-xs"
                                value={successMessage}
                                placeholder="Thank you!"
                                onChange={event => {
                                    setNodeState({ ...nodeState, successMessage: event.target.value });
                                }}
                            />
                        </div>

                        {/* Redirect URL */}
                        <div>
                            <label className="text-[10px] opacity-60 block mb-0.5">Redirect URL</label>
                            <Input
                                className="h-7 text-xs"
                                value={redirectUrl}
                                placeholder="https://..."
                                onChange={event => {
                                    setNodeState({ ...nodeState, redirectUrl: event.target.value });
                                }}
                            />
                        </div>
                    </NodeEventInsulator>
                </div>
            </div>
        </div>
    );
};
